import { type FC, type HTMLAttributes } from 'react';

import { Box } from '../Box';
import { type IconVariantTypes, Icon } from '../Icon';
import { Text } from '../Typography';
import {
  baseStyles,
  checkboxCheckedStyles,
  checkboxStyles,
  checkedStyles,
} from './AddOnBlock.styles';

export type AddOnBlockProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  'onClick'
> & {
  /**
   * Title text displayed prominently
   */
  title: string;
  /**
   * Subtitle text displayed below the title
   */
  subtitle: string;
  /**
   * Icon variant to display on the right
   */
  icon: IconVariantTypes;
  /**
   * Whether the add-on is checked/selected
   */
  checked?: boolean;
  /**
   * Callback when the block is clicked
   */
  onClick?: () => void;
  /**
   * Additional CSS class names
   */
  className?: string;
};

const AddOnBlock: FC<AddOnBlockProps> = ({
  title,
  subtitle,
  icon,
  checked = false,
  onClick,
  className,
  ...rest
}) => {
  return (
    <Box
      css={[baseStyles, checked && checkedStyles]}
      onClick={onClick}
      className={className}
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.();
        }
      }}
      {...rest}
    >
      <Box>
        <Box css={[checkboxStyles, checked && checkboxCheckedStyles]}>
          {checked && (
            <Icon variant="Check" size="small" fill="var(--border-action)" />
          )}
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" flex={1}>
        <Text as="span" size="md" fontWeight="bold">
          {title}
        </Text>
        <Text as="span" size="sm" fontWeight="normal">
          {subtitle}
        </Text>
      </Box>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="var(--icon-primary)"
      >
        <Icon variant={icon} size="large" />
      </Box>
    </Box>
  );
};

AddOnBlock.displayName = 'AddOnBlock';

export default AddOnBlock;
